/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { DatePipe } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatTableDataSource } from '@angular/material/table'
import { ActivatedRoute } from '@angular/router'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { ConfirmDialogModel, ConfirmDialogComponent } from 'src/app/modules/confirm-dialog/confirm-dialog.component'
import { FaseSelecaoDialogComponent } from '../fase-selecao-dialog/fase-selecao-dialog.component'
import { TipoConcurso } from 'src/app/enums/tipo-concurso'
import { TipoProva, TipoProvaVersao } from 'src/app/enums/tipo-prova'
import { ConcursoProva } from 'src/app/models/concurso-prova'
import { ConcursoProvaService } from 'src/app/services/concurso-prova.service'
import { ProvaFase, ProvaFaseLabel } from 'src/app/enums/prova-fase';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-provas-gerencia',
  templateUrl: './provas-gerencia.component.html',
  styleUrls: ['./provas-gerencia.component.scss'],
})
export class ProvasGerenciaComponent implements OnInit {
  idConcurso?: number
  concursoTitulo?: string
  concursoTipo?: string
  concursoTipoRaw?: string

  ProvaFase = ProvaFase
  ProvaFaseLabel = ProvaFaseLabel

  colunasGrid: string[] = ['tipoProva', 'fase', 'ordemProvas', 'pesoProvas', 'acoes']
  dataSource: MatTableDataSource<ConcursoProva> = new MatTableDataSource()

  tipoProva: Map<string, string> = new Map()
  adicionarProvaFC = new FormControl()
  addProvaLabel: string = ''
  temProvaConcluida: boolean = false

  datepipe: DatePipe = new DatePipe('pt-BR')

  listaFases: ProvaFase[] = [ProvaFase.FASE1, ProvaFase.FASE2];
  ordemOriginalMap: { [provaId: string]: number | undefined } = {};
  
  valorAnteriorOrdem: number | null | undefined = null;
  valorAnteriorPeso: number | null | undefined = null;
  valorAnteriorFase: string | null | undefined = null;
  valorAnteriorDataInicial: string | null | undefined = null;
  valorAnteriorDataInicioDocumentos: string | null | undefined = null;

  constructor(
    private unespCoreMessageService: UnespCoreMessageService,
    private concursoProvaService: ConcursoProvaService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    ;(this.route.parent?.params || this.route.params).subscribe(params => {
      this.idConcurso = params['id'] || params['idConcurso']
      if (this.idConcurso) {
        this.initTable()
        this.adicionarProvaFC.valueChanges.subscribe(value => {
          if (value) {
            this.adicionarProva(value)
            this.adicionarProvaFC.reset()
          }
        })
      }
    })
  }

  public initTable() {
    this.concursoProvaService.listar(this.idConcurso!).subscribe(concurso => {      
      concurso.provas.forEach(prova => {
        prova.provaFase = this.converterProvaFase(prova.provaFase) ?? undefined;
        prova.provaOrdem = prova.provaOrdem ?? undefined;
        prova.provaPeso = prova.provaPeso ?? undefined;
        prova.dataInicio = prova.dataInicio ?? undefined;
        prova.dataInicioDocumentos = prova.dataInicioDocumentos ?? undefined;
      });
  
      this.dataSource = new MatTableDataSource(concurso.provas);
      this.atualizaTemProvaConcluida();
      this.concursoTitulo = concurso.titulo;
      this.concursoTipo = (TipoConcurso as any)[concurso.tipo];
      this.concursoTipoRaw = concurso.tipo as unknown as string;
      this.initTipoProva(concurso.provas);
    });
  }
  
  private converterProvaFase(fase: string | null | undefined) {
    switch (fase) {
      case 'FASE1':
        return ProvaFase.FASE1;
      case 'FASE2':
        return ProvaFase.FASE2;
      default:
        return undefined;
    }
  }

  private initTipoProva(provas: ConcursoProva[]) {
    for (const tipo in TipoProva) this.tipoProvaAddOpcao(tipo)

    for (const prova of provas) this.tipoProvaRemoveOpcao(prova.tipoProva)
  }

  get provasPrimeiraFase() {
    return this.dataSource?.data?.filter(p => p.provaFase === ProvaFase.FASE1) || [];
  }
  
  get provasSegundaFase() {
    return this.dataSource?.data?.filter(p => p.provaFase === ProvaFase.FASE2) || [];
  }
  
  get provasSemFase() {
    return this.dataSource?.data?.filter(p => !p.provaFase) || [];
  }

  private tipoProvaAddOpcao(tipo: string) {
    for (const versao in TipoProvaVersao)
      if (versao.startsWith(tipo)) this.tipoProva.set(versao, (TipoProvaVersao as any)[versao].desc)

    //this.tipoProva.set(tipo, (TipoProva as any)[tipo])
    if (this.tipoProva.size > 0) {
      this.addProvaLabel = 'Adicionar prova disponível'
      this.adicionarProvaFC.enable()
    }
  }

  private tipoProvaRemoveOpcao(tipo: string) {
    for (const versao in TipoProvaVersao) if (versao.startsWith(tipo)) this.tipoProva.delete(versao)

    //this.tipoProva.delete(tipo)
    if (this.tipoProva.size == 0) {
      this.addProvaLabel = 'Todas as provas foram adicionadas'
      this.adicionarProvaFC.disable()
    }
  }

  private readonly PROVAS_FASE_FIXA_PT = ['TITULOS', 'DIDATICA', 'ARGUICAO']
  private readonly TIPOS_COM_FASE_FIXA = ['PT', 'PTPP']

  private adicionarProva(versaoStr: string) {
    const versaoObj = (TipoProvaVersao as any)[versaoStr]
    const tipoProva: string = versaoObj.tipo

    const pularDialog = this.concursoTipoRaw
      && this.TIPOS_COM_FASE_FIXA.includes(this.concursoTipoRaw)
      && this.PROVAS_FASE_FIXA_PT.includes(tipoProva)

    if (pularDialog) {
      this.gravarProva(tipoProva, versaoStr)
      return
    }

    const dialogRef = this.dialog.open(FaseSelecaoDialogComponent)
    dialogRef.afterClosed().subscribe(fase => {
      if (fase) {
        this.gravarProva(tipoProva, versaoStr, fase)
      }
    })
  }

  private gravarProva(tipoProva: string, versaoStr: string, provaFase?: string) {
    this.concursoProvaService
      .gravar({ idConcurso: this.idConcurso!, tipoProva, tipoProvaVersao: versaoStr, provaFase })
      .subscribe(prova => {
        this.tipoProvaRemoveOpcao(prova.tipoProva)
        this.initTable()
      })
  }

  salvarValorAnteriorFase(prova: ConcursoProva) {
    this.valorAnteriorFase = prova.provaFase;
  }

  salvarValorAnteriorOrdem(prova: ConcursoProva) {    
    this.valorAnteriorOrdem = prova.provaOrdem;
  }

  salvarValorAnteriorPeso(prova: ConcursoProva) {    
    this.valorAnteriorPeso = prova.provaPeso;
  }

  private recarregarTabelaOrdenada() {
    const provasOrdenadas = this.dataSource.data.sort((a, b) => (a.provaOrdem ?? 0) - (b.provaOrdem ?? 0));
    this.dataSource = new MatTableDataSource(provasOrdenadas);
  }

  atualizarFase(prova: ConcursoProva, novaFase: string | undefined) {
    // Salva o valor atual antes de qualquer modificação
    const faseAnterior = prova.provaFase;
    
    if (novaFase === undefined) {
      prova.provaFase = faseAnterior ?? '';
      return;
    }
  
    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      `Confirma a atualização da fase da Prova ${this.tipoProvaStr(prova.tipoProva)} para ${ProvaFaseLabel[novaFase as ProvaFase] || novaFase}?`
    );
  
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog });
  
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        // Atualiza temporariamente para o novo valor (feedback visual)
        prova.provaFase = novaFase;
        
        this.concursoProvaService.atualizarFase(prova.idConcurso, prova.tipoProva, novaFase)
          .subscribe({
            next: (result: ConcursoProva) => {
              const idx = this.dataSource.data.indexOf(prova);
              if (idx !== -1) {
                this.dataSource.data[idx].provaFase = this.converterProvaFase(result.provaFase);
                this.dataSource.data[idx].provaOrdem = result.provaOrdem;                
                this.dataSource.data = [...this.dataSource.data];                
                this.unespCoreMessageService.showMessageSuccess('Fase atualizada com sucesso!');
                this.initTable();
              }
            },
            error: (err) => {
              // Restaura o valor anterior em caso de erro
              prova.provaFase = faseAnterior ?? '';
              const mensagemErro = err?.error?.message ?? 'Erro ao atualizar fase.';
              this.unespCoreMessageService.showMessageError(mensagemErro);
              this.cdr.detectChanges();
            }
          });
      } else {
        // Restaura o valor anterior se o usuário cancelar
        prova.provaFase = faseAnterior ?? '';
        this.cdr.detectChanges();
      }
    });
}

atualizarOrdem(prova: ConcursoProva, novaOrdem: number | undefined) {
    const ordemAnterior = prova.provaOrdem;
    
    if (novaOrdem === undefined) {
      prova.provaOrdem = ordemAnterior ?? undefined;
      return;
    }

    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      `Confirma a ${novaOrdem === null ? 'remoção' : 'atualização'} da ordem da Prova ${this.tipoProvaStr(prova.tipoProva)}${novaOrdem !== null ? ` para ${novaOrdem}` : ''}?`
    );

    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        prova.provaOrdem = novaOrdem;

        this.concursoProvaService.atualizarOrdem(prova.idConcurso, prova.tipoProva, novaOrdem)
          .subscribe({
            next: () => {
              this.unespCoreMessageService.showMessageSuccess('Ordem atualizada com sucesso!');
              this.initTable();
            },
            error: (err) => {
              prova.provaOrdem = ordemAnterior ?? undefined;
              const mensagemErro = err?.error?.message ?? 'Erro ao atualizar ordem.';
              this.unespCoreMessageService.showMessageError(mensagemErro);
              this.cdr.detectChanges();
            }
          });
      } else {
        prova.provaOrdem = ordemAnterior ?? undefined;
        this.cdr.detectChanges();
      }
    });
  }

  atualizarPeso(prova: ConcursoProva, novoPeso: number | undefined) {               
    if (novoPeso === undefined) {      
      return;
    }

    if (novoPeso !== null && novoPeso < 0) {
      prova.provaPeso = this.valorAnteriorPeso ?? undefined;
      this.unespCoreMessageService.showMessageError('O peso não pode ser negativo.');
      return;
    }
  
    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      `Confirma a ${novoPeso === null ? 'remoção' : 'atualização'} do Peso da Prova ${this.tipoProvaStr(prova.tipoProva)}${novoPeso !== null ? ` para ${novoPeso}` : ''}?`
    );
  
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog });
  
    dialogRef.afterClosed().subscribe(res => {      
      if (res) {                
        this.concursoProvaService.atualizarPeso(prova.idConcurso, prova.tipoProva, novoPeso)
          .subscribe({
            next: (result) => {
              let tmp = this.dataSource.data;
              const idx = tmp.indexOf(prova);
              tmp[idx].provaPeso = novoPeso;
              this.dataSource.data = [...tmp];
            },
            error: (err) => {              
              prova.provaPeso = this.valorAnteriorPeso ?? undefined;            
              this.unespCoreMessageService.showMessageError(err.error.message)
            }
          });
      } else {
        prova.provaPeso = this.valorAnteriorPeso ?? undefined;
      }
    });
  }

  atualizarDataInicial(prova: ConcursoProva, dataInicio: string | undefined) {               
    if (dataInicio === undefined || dataInicio === this.valorAnteriorDataInicial) {      
      return;
    }

    if (dataInicio === ''){
      alert("Por favor, informe a data e hora do início da prova!");
      return;
    }      
  
    console.log("Data: " + dataInicio);
    const dataFormatada = dataInicio ? dataInicio.split('T')[0].split('-').reverse().join('/') : null;
    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      `Confirma a ${dataInicio === null ? 'remoção' : 'atualização'} da data inicial da Prova ${this.tipoProvaStr(prova.tipoProva)}${dataFormatada ? ` para ${dataFormatada}` : ''}?`
    );
  
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog });
  
    dialogRef.afterClosed().subscribe(res => {      
      if (res) {                
        this.concursoProvaService.atualizarDataInicio(prova.idConcurso, prova.tipoProva, dataInicio)
          .subscribe({
            next: (result) => {
              let tmp = this.dataSource.data;
              const idx = tmp.indexOf(prova);
              tmp[idx].dataInicio = dataInicio;
              this.dataSource.data = [...tmp];
            },
            error: (err) => {              
              prova.dataInicio = this.valorAnteriorDataInicial ?? undefined;            
              this.unespCoreMessageService.showMessageError(err.error.message)
            }
          });
      } else {
        prova.dataInicio = this.valorAnteriorDataInicial ?? undefined;
      }
    });
  }

  excluir(prova: ConcursoProva) {
    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      `Confirma a exclusão da Prova ${this.tipoProvaStr(prova.tipoProva)}`
    )
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: confirmDialog,
    })
    dialogRef.afterClosed().subscribe(res => {
      if (res)
        this.concursoProvaService.excluir(prova.idConcurso, prova.tipoProva).subscribe(result => {
          if (result) {
            let tmp = this.dataSource.data
            const idx = tmp.indexOf(prova)
            tmp.splice(idx, 1)
            this.dataSource.data = tmp
            this.tipoProvaAddOpcao(prova.tipoProva)
            this.initTable();
          }
        })
    })
  }

  divulgar(prova: ConcursoProva) {
    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      `Confirma a divulgação da Prova ${this.tipoProvaStr(prova.tipoProva)}`
    )
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: confirmDialog,
    })
    dialogRef.afterClosed().subscribe(res => {
      if (res)
        this.concursoProvaService.publicar(prova.idConcurso, prova.tipoProva).subscribe(result => {
          if (result) {
            let tmp = this.dataSource.data
            const idx = tmp.indexOf(prova)
            tmp[idx].publicado = true
            this.dataSource.data = tmp
          }
        })
    })
  }

  concluir(prova: ConcursoProva) {
    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      `Confirma a conclusão da Prova ${this.tipoProvaStr(prova.tipoProva)}`
    )
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: confirmDialog,
    })
    dialogRef.afterClosed().subscribe(res => {
      if (res)
        this.concursoProvaService.concluir(prova.idConcurso, prova.tipoProva).subscribe(result => {
          if (result) {
            let tmp = this.dataSource.data
            const idx = tmp.indexOf(prova)
            tmp[idx].concluido = true
            this.dataSource.data = tmp
            this.atualizaTemProvaConcluida()
          }
        })
    })
  }

  tipoProvaStr(tipo: string): string {
    return (TipoProva as any)[tipo].tipo
  }

  private atualizaTemProvaConcluida() {
    let provaConcluidaBool = false

    for (let prova of this.dataSource.data) if (prova.concluido) provaConcluidaBool = true

    this.temProvaConcluida = provaConcluidaBool
  }

  gerarResultadoFinal() {
    this.concursoProvaService.gerarResultadoFinal(this.idConcurso!)
  }

  deveExibirDataInicioDocumentos(prova: ConcursoProva): boolean {
    return prova.provaOrdem === 1 &&            
           prova.tipoProva !== 'ESCRITA';
  }

  atualizarDataInicioDocumentos(prova: ConcursoProva, dataInicioDocumentos: string | undefined) {               
    if (dataInicioDocumentos === undefined || dataInicioDocumentos === this.valorAnteriorDataInicioDocumentos) {      
      return;
    }

    if (dataInicioDocumentos === ''){
      alert("Por favor, informe a data de início dos documentos!");
      return;
    }      
  
    const dataFormatada = dataInicioDocumentos ? dataInicioDocumentos.split('T')[0].split('-').reverse().join('/') : null;
    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      `Confirma a ${dataInicioDocumentos === null ? 'remoção' : 'atualização'} da data de início dos documentos da Prova ${this.tipoProvaStr(prova.tipoProva)}${dataFormatada ? ` para ${dataFormatada}` : ''}?`
    );
  
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog });
  
    dialogRef.afterClosed().subscribe(res => {      
      if (res) {                
        this.concursoProvaService.atualizarDataInicioDocumentos(prova.idConcurso, prova.tipoProva, dataInicioDocumentos)
          .subscribe({
            next: (result) => {
              let tmp = this.dataSource.data;
              const idx = tmp.indexOf(prova);
              tmp[idx].dataInicioDocumentos = dataInicioDocumentos;
              this.dataSource.data = [...tmp];
            },
            error: (err) => {              
              prova.dataInicioDocumentos = this.valorAnteriorDataInicioDocumentos ?? undefined;            
              this.unespCoreMessageService.showMessageError(err.error.message)
            }
          });
      } else {
        prova.dataInicioDocumentos = this.valorAnteriorDataInicioDocumentos ?? undefined;
      }
    });
  }
}
